import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sun, PlugZap, ThermometerSun, Flame, Snowflake, ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeUp } from "../components/Reveal";

const SERVICES = [
    {
        n: "01",
        icon: Sun,
        title: "Fotovoltaico",
        desc: "Progettazione e installazione di impianti fotovoltaici residenziali e aziendali, anche con accumulo.",
        span: "md:col-span-3",
        id: "fotovoltaico",
    },
    {
        n: "02",
        icon: PlugZap,
        title: "Wallbox",
        desc: "Installazione di stazioni di ricarica per veicoli elettrici.",
        span: "md:col-span-3",
        id: "wallbox",
    },
    {
        n: "03",
        icon: ThermometerSun,
        title: "Pompe di calore",
        desc: "Riscaldamento e acqua calda sanitaria ad alta efficienza.",
        span: "md:col-span-2",
        id: "pompe-di-calore",
    },
    {
        n: "04",
        icon: Flame,
        title: "Caldaie",
        desc: "Installazione e sostituzione di caldaie.",
        span: "md:col-span-2",
        id: "caldaie",
    },
    {
        n: "05",
        icon: Snowflake,
        title: "Climatizzazione",
        desc: "Installazione di climatizzatori e sistemi di raffrescamento.",
        span: "md:col-span-2",
        id: "climatizzazione",
    },
];

const MARQUEE_ITEMS = ["Fotovoltaico", "Wallbox", "Pompe di calore", "Caldaie", "Climatizzazione"];

function Hero() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const wordX = useTransform(scrollYProgress, [0, 1], ["4%", "-14%"]);
    const wordOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.4]);

    return (
        <section ref={heroRef} className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden px-6 pb-14 pt-32 md:px-12 md:pb-20">
            <motion.div
                aria-hidden="true"
                style={{ x: wordX, opacity: wordOpacity }}
                className="text-outline stretch-wide pointer-events-none absolute -bottom-[3vw] right-0 select-none whitespace-nowrap text-[27vw] font-black leading-none tracking-[-0.04em]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.4, delay: 0.5 }}
            >
                ECOHOUSE
            </motion.div>

            <div className="relative">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.15 }}
                    className="mb-8 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-leafdeep/70 md:mb-12 md:text-xs"
                >
                    <motion.span
                        className="inline-block h-px w-10 origin-left bg-leaf/60"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    />
                    Ecohouse S.r.l.s.
                </motion.p>

                <h1
                    data-testid="hero-title"
                    className="stretch-wide max-w-[12ch] text-[clamp(2.9rem,9.5vw,9rem)] font-black leading-[0.88] tracking-[-0.035em] text-leaf"
                >
                    <MaskedLine delay={0.1}>Energia pulita</MaskedLine>
                    <MaskedLine delay={0.22}>per casa</MaskedLine>
                    <MaskedLine delay={0.34}>
                        e <em className="font-light italic">azienda</em>
                    </MaskedLine>
                </h1>

                <FadeUp delay={0.55} y={20}>
                    <p data-testid="hero-subtitle" className="mt-8 max-w-[36rem] text-base leading-relaxed text-leafdeep/80 md:mt-10 md:text-lg">
                        Installiamo impianti fotovoltaici, wallbox, pompe di calore, caldaie e climatizzatori a Vercelli e provincia.
                    </p>
                </FadeUp>

                <FadeUp delay={0.7} y={20}>
                    <a
                        href="mailto:info@ecohouse.srl"
                        data-testid="hero-cta-mailto"
                        className="group relative mt-10 inline-flex items-center gap-3 overflow-hidden border border-ink bg-ink px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-paper md:mt-12"
                    >
                        <span className="absolute inset-0 origin-bottom scale-y-0 bg-moss transition-transform duration-300 ease-out group-hover:scale-y-100" aria-hidden="true" />
                        <span className="relative">Richiedi informazioni</span>
                        <ArrowUpRight size={18} strokeWidth={1.75} className="relative transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                </FadeUp>
            </div>
        </section>
    );
}

function Marquee() {
    return (
        <div className="overflow-hidden border-y border-ink bg-ink py-4 text-paper md:py-5" data-testid="services-marquee">
            <div className="marquee-track flex w-max items-center whitespace-nowrap">
                {[0, 1].map((set) => (
                    <div key={set} className="flex items-center" aria-hidden={set === 1}>
                        {MARQUEE_ITEMS.map((item, i) => (
                            <span
                                key={`${set}-${i}`}
                                className="stretch-wide flex items-center gap-8 pr-8 text-xl font-bold tracking-tight md:gap-14 md:pr-14 md:text-3xl"
                            >
                                {item}
                                <span className="text-2xl text-paper/40 md:text-3xl" aria-hidden="true">•</span>
                            </span>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}

function Services() {
    return (
        <section id="servizi" className="px-6 py-24 md:px-12 md:py-36">
            <FadeUp>
                <p className="mb-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-leafdeep/70 md:text-xs">
                    <span className="inline-block h-px w-10 bg-leaf/60" aria-hidden="true" />
                    Servizi
                </p>
                <h2 className="stretch-wide mb-14 text-5xl font-black tracking-[-0.03em] md:mb-20 md:text-7xl text-leaf" data-testid="services-title">
                    Cosa facciamo<span className="text-leaf/30">.</span>
                </h2>
            </FadeUp>

            <div className="grid grid-cols-1 gap-px border border-hairline bg-hairline md:grid-cols-6">
                {SERVICES.map((s, i) => (
                    <FadeUp key={s.id} delay={i * 0.07} className={s.span}>
                        <article
                            data-testid={`service-card-${s.id}`}
                            className="group flex h-full flex-col bg-paper p-8 transition-colors duration-300 hover:bg-ink hover:text-paper md:p-10"
                        >
                            <div className="flex items-start justify-between">
                                <span className="font-mono text-xs text-leafdeep/60 transition-colors duration-300 group-hover:text-paper/50">{s.n}</span>
                                <s.icon size={30} strokeWidth={1.25} className="transition-colors duration-300" />
                            </div>
                            <h3 className="stretch-wide mt-16 text-2xl font-extrabold tracking-tight text-leaf transition-colors duration-300 group-hover:text-paper md:mt-24 md:text-3xl">
                                {s.title}
                            </h3>
                            <p className="mt-3 max-w-[30rem] text-sm leading-relaxed text-leafdeep/80 transition-colors duration-300 group-hover:text-paper/70 md:text-base">
                                {s.desc}
                            </p>
                        </article>
                    </FadeUp>
                ))}
            </div>
        </section>
    );
}

function About() {
    return (
        <section id="chi-siamo" className="border-t border-hairline px-6 py-24 md:px-12 md:py-36">
            <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
                <FadeUp className="md:col-span-3">
                    <p className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-leafdeep/70 md:text-xs">
                        <span className="inline-block h-px w-10 bg-leaf/60" aria-hidden="true" />
                        Chi siamo
                    </p>
                </FadeUp>
                <div className="md:col-span-9">
                    <FadeUp delay={0.1}>
                        <p data-testid="chi-siamo-text" className="text-2xl font-medium leading-[1.3] tracking-tight text-leaf md:text-4xl md:leading-[1.25]">
                            Ecohouse S.r.l.s. è un'impresa di installazione impiantistica con sede in provincia di Vercelli.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.2}>
                        <p className="mt-8 max-w-[44rem] text-base leading-relaxed text-ink/70 md:text-lg">
                            Operiamo come installatori qualificati ai sensi del{" "}
                            <span className="whitespace-nowrap font-mono text-sm font-semibold text-leaf md:text-base">D.M. 37/2008</span>{" "}
                            e collaboriamo con i principali operatori del settore energia.
                        </p>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
}

function Contacts() {
    const cells = [
        { label: "Sede legale", value: "Tronzano Vercellese (VC)" },
        { label: "Sede operativa", value: "Vercelli" },
        { label: "P.IVA", value: "02779610027", mono: true },
    ];
    return (
        <section id="contatti" className="bg-ink px-6 py-24 text-paper md:px-12 md:py-36">
            <FadeUp>
                <p className="mb-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-paper/60 md:text-xs">
                    <span className="inline-block h-px w-10 bg-paper/50" aria-hidden="true" />
                    Contatti
                </p>
                <h2 className="stretch-wide mb-6 text-5xl font-black tracking-[-0.03em] md:mb-10 md:text-7xl" data-testid="contacts-title">
                    Parliamone<span className="text-paper/30">.</span>
                </h2>
                <p className="text-base text-paper/70 md:text-lg" data-testid="contacts-company">
                    Ecohouse S.r.l.s.
                </p>
            </FadeUp>

            <FadeUp delay={0.1}>
                <div className="mt-14 grid grid-cols-1 gap-px border border-paper/15 bg-paper/15 sm:grid-cols-2 lg:grid-cols-4">
                    {cells.map((c) => (
                        <div key={c.label} className="bg-ink p-7 md:p-8" data-testid={`contacts-cell-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-paper/50">{c.label}</p>
                            <p className={`mt-4 text-lg font-semibold md:text-xl ${c.mono ? "font-mono tracking-tight" : ""}`}>{c.value}</p>
                        </div>
                    ))}
                    <div className="bg-ink p-7 md:p-8" data-testid="contacts-cell-email">
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-paper/50">Email</p>
                        <a
                            href="mailto:info@ecohouse.srl"
                            data-testid="contacts-email-link"
                            className="mt-4 inline-flex items-center gap-2 text-lg font-semibold underline decoration-paper/40 underline-offset-8 transition-colors hover:decoration-paper md:text-xl"
                        >
                            info@ecohouse.srl
                            <ArrowUpRight size={18} strokeWidth={1.75} />
                        </a>
                    </div>
                </div>
            </FadeUp>

            <FadeUp delay={0.15}>
                <a
                    href="mailto:info@ecohouse.srl"
                    data-testid="contacts-cta-mailto"
                    className="group relative mt-14 inline-flex items-center gap-3 overflow-hidden border border-paper px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-paper"
                >
                    <span className="absolute inset-0 origin-bottom scale-y-0 bg-paper transition-transform duration-300 ease-out group-hover:scale-y-100" aria-hidden="true" />
                    <span className="relative transition-colors duration-300 group-hover:text-ink">Richiedi informazioni</span>
                    <ArrowUpRight size={18} strokeWidth={1.75} className="relative transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-ink" />
                </a>
            </FadeUp>
        </section>
    );
}

export default function Home() {
    return (
        <>
            <Hero />
            <Marquee />
            <Services />
            <About />
            <Contacts />
        </>
    );
}
